import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import {
  MotifButton,
  MotifFormField,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
  MotifRadioButton,
  MotifRadioButtonGroup,
  MotifToggleSwitch,
  MotifErrorMessage,
  MotifProgressLoader,
  MotifToast,
} from "@ey-xd/motif-react";
import {
  resetSearchUsers,
  searchUsers,
} from "../../store/slices/CTDBProductContactListSlice";
import {
  fetchAdminSupportListData,
  resetAddUserError,
  addAdminSupportUser,
  SystemRoles,
  editAdminSupportUser,
  resetEditUserError,
} from "../../store/slices/AdminSupportSlice";
import SearchInput from "../../pages/CTProductCostSetup/Components/SearchInput/SearchInput";
import ConfirmationModal from "../ConfirmationModal";
import productListEmptyImg from "../../assets/images/empty_product_list_default.svg";
import ErrorOrderPage from "../ErrorOrderPage/ErrorOrderPage";
import "./AdminSupportModal.scss";

interface AdminSupportUser {
  displayName: string;
  email: string;
}

type AdminSupportRole = "system_admin" | "system_support" | "";

type AdminSupportRow = Record<
  string,
  string | number | boolean | string[] | null | undefined
>;

interface AdminSupportModalProps {
  show: boolean;
  onClose: (type: "discard" | "add" | "edit" | "cancel") => void;
  isEdit?: boolean;
  adminData?: AdminSupportRow | null;
  setShowAdminSupportModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

const formatUserOption = (user: AdminSupportUser) =>
  `${user.displayName} (${user.email})`;

const formatSelectedUser = (user: AdminSupportUser) => user.email;

const AdminSupportModal = ({
  onClose,
  show,
  isEdit = false,
  adminData = null,
  setShowAdminSupportModal,
}: AdminSupportModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<AdminSupportUser | null>(
    null,
  );
  const [selectedRole, setSelectedRole] = useState<AdminSupportRole>("");
  const [isActive, setIsActive] = useState(true);
  const [userValidationError, setUserValidationError] = useState("");
  const [roleValidationError, setRoleValidationError] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [searchInputKey, setSearchInputKey] = useState(0);

  const searchAbortRef = useRef<{ abort: () => void } | null>(null);

  const isAdminListLoading = useSelector(
    (state: RootState) => state.AdminSupportSlice.modalIsLoading,
  );
  const adminListData = useSelector(
    (state: RootState) => state.AdminSupportSlice.modalData,
  );
  const adminListError = useSelector(
    (state: RootState) => state.AdminSupportSlice.modalError,
  );

  const userList = useSelector(
    (state: RootState) => state.CTDBProductContactListSlice.data,
  );
  const userListLoading = useSelector(
    (state: RootState) => state.CTDBProductContactListSlice.isLoading,
  );

  const isAddingUser = useSelector(
    (state: RootState) => state.AdminSupportSlice.isAddingUser,
  );

  const addUserError = useSelector(
    (state: RootState) => state.AdminSupportSlice.addUserError,
  );

  const isEditingUser = useSelector(
    (state: RootState) => state.AdminSupportSlice.isEditingUser,
  );

  const editUserError = useSelector(
    (state: RootState) => state.AdminSupportSlice.editUserError,
  );
  const getRoleValue = (roleCd: unknown): AdminSupportRole => {
    const role = String(roleCd || "").toLowerCase();
    return role === "system_admin" || role === "system_support" ? role : "";
  };
  useEffect(() => {
    dispatch(resetSearchUsers());
    return () => {
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
      }
      dispatch(resetSearchUsers());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!show || !isEdit || !adminData) {
      return;
    }
    const displayName =
      typeof adminData.Name === "string" ? adminData.Name : "";
    const email = typeof adminData.Email === "string" ? adminData.Email : "";
    const role = getRoleValue(adminData.RoleCd);

    setSelectedUser({ displayName, email });
    setUserSearch(formatUserOption({ displayName, email }));
    setSelectedRole(
      role === "system_admin" || role === "system_support" ? role : "",
    );
    setIsActive(Boolean(adminData.IsActive));
    setIsExistingUser(false);
    setUserValidationError("");
    setRoleValidationError("");
  }, [isEdit, adminData, show]);

  useEffect(() => {
    focusUserSearchInput();
  }, [selectedUser]);

  useEffect(() => {
    dispatch(
      fetchAdminSupportListData({
        page: 1,
        record: 1000,
        search: "",
        sortOrder: "ASC",
        sortColumn: "Name",
        target: "modal",
      }),
    );
  }, []);

  useEffect(() => {
    if (!show) return;

    const body = document.body;
    const originalOverflow = body.style.overflow;
    const originalPaddingRight = body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      body.style.overflow = originalOverflow;
      body.style.paddingRight = originalPaddingRight;
    };
  }, [show]);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    }
  }, [show, showConfirmationModal]);

  const filteredItems = Array.isArray(userList)
    ? (userList as AdminSupportUser[]).filter((user) => {
        const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
        return emailRegex.test(user.email);
      })
    : [];

  const displayItems = filteredItems.map(formatUserOption);

  const isUserAlreadyAdminOrSupport = (user: AdminSupportUser) =>
    Boolean(
      adminListData?.Result?.length &&
      adminListData.Result.some(
        (admin) =>
          typeof admin.Email === "string" &&
          admin.Email.toLowerCase() === user.email.toLowerCase(),
      ),
    );

  const focusUserSearchInput = () => {
    window.setTimeout(() => {
      document.getElementById("admin-support-user-search")?.focus();
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    clearAddUserError();
    clearEditUserError();
    const value = event.target.value;
    const searchValue = value.trim();
    const isSameSelectedUser = Boolean(
      selectedUser && searchValue === formatSelectedUser(selectedUser),
    );
    setUserSearch(value);
    if (!isSameSelectedUser) {
      setUserValidationError("");
    }
    if (isSameSelectedUser) {
      dispatch(resetSearchUsers());
      return;
    }
    if (selectedUser && !isSameSelectedUser) {
      setSelectedUser(null);
      setIsExistingUser(false);
      setSelectedRole("");
      setIsActive(true);
      setRoleValidationError("");
    }
    if (searchValue === "") {
      setSelectedUser(null);
      setIsExistingUser(false);
      setSelectedRole("");
      setIsActive(true);
      dispatch(resetSearchUsers());
    }
    if (searchValue.length >= 3) {
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
      }
      dispatch(resetSearchUsers());
      const promise = dispatch(searchUsers({ searchText: searchValue }));
      searchAbortRef.current = promise as unknown as { abort: () => void };
    }
  };
  const handleUserSelect = (option: string | AdminSupportUser | undefined) => {
    if (!option) return;
    const user = {
      displayName: (option as string).split("(")[0].trim(),
      email: (option as string).split("(")[1].split(")")[0].trim(),
    };

    if (!user) return;
    const alreadyExists = isUserAlreadyAdminOrSupport(user);
    setSearchInputKey((currentKey) => currentKey + 1);
    setSelectedUser(user);
    setIsExistingUser(alreadyExists);
    setUserSearch(formatSelectedUser(user));
    setUserValidationError(
      alreadyExists
        ? `${user.displayName} already exists as an admin or support. Please select another user to proceed.`
        : "",
    );
    setRoleValidationError("");
    dispatch(resetSearchUsers());
    focusUserSearchInput();
  };

  const editUser = async () => {
    if (!selectedUser) {
      return;
    }
    try {
      await dispatch(
        editAdminSupportUser({
          Action: "Edit",
          Name: selectedUser.displayName,
          Email: selectedUser.email,
          IsActive: isActive,
          Role: selectedRole as SystemRoles,
        }),
      ).unwrap();
      setSelectedUser(null);
      setUserSearch("");
      setShowAdminSupportModal && setShowAdminSupportModal(false);
      onClose("edit");
    } catch {
      // error is handled by Redux state (editUserError) and shown in modal toast
    }
  };

  const handleEdit = async () => {
    if (!selectedUser) {
      setUserValidationError(
        userSearch.trim()
          ? "Please enter valid user name or email."
          : "Please enter user name or email.",
      );
      setRoleValidationError("");
      return;
    }
    if (isExistingUser) {
      setRoleValidationError("");
      return;
    }
    setUserValidationError("");
    if (!selectedRole) {
      setRoleValidationError("Please select a role.");
      return;
    }
    setRoleValidationError("");
    await editUser();
  };

  const handleAdd = async () => {
    if (!selectedUser) {
      setUserValidationError(
        userSearch.trim()
          ? "Please enter valid user name or email."
          : "Please enter user name or email.",
      );
      setRoleValidationError("");
      return;
    }
    if (isExistingUser) {
      setRoleValidationError("");
      return;
    }
    setUserValidationError("");
    if (!selectedRole) {
      setRoleValidationError("Please select a role.");
      return;
    }
    setRoleValidationError("");
    await createUser();
  };

  const createUser = async () => {
    if (!selectedUser) {
      return;
    }
    await dispatch(
      addAdminSupportUser({
        Action: "Add",
        Name: selectedUser.displayName,
        Email: selectedUser.email,
        IsActive: isActive,
        Role: selectedRole as SystemRoles,
      }),
    ).unwrap();
    setSelectedUser(null);
    setUserSearch("");
    handleClose("add");
  };

  const handleClearSearch = (event?: React.MouseEvent<HTMLButtonElement>) => {
    clearAddUserError();
    clearEditUserError();
    if (
      !event?.currentTarget.classList.contains("motif-typeahead-clear-button")
    ) {
      return;
    }
    setUserSearch("");
    setSelectedUser(null);
    setIsExistingUser(false);
    setSelectedRole("");
    setIsActive(true);
    setUserValidationError("");
    setRoleValidationError("");
    dispatch(resetSearchUsers());
    focusUserSearchInput();
  };

  const handleModalClose = () => {
    if (isAddingUser || isEditingUser) return;
    if (isEdit) {
      const hasEditChanges =
        isActive !== Boolean(adminData?.IsActive) ||
        selectedRole !== getRoleValue(adminData?.RoleCd);

      if (hasEditChanges) {
        setShowConfirmationModal(true);
      } else {
        handleClose("cancel");
      }
      return;
    }

    if (selectedUser || userSearch.trim()) {
      setShowConfirmationModal(true);
      return;
    }
    handleClose("cancel");
  };

  const handleClose = (action: "discard" | "add" | "edit" | "cancel") => {
    onClose(action);
  };

  const clearAddUserError = () => dispatch(resetAddUserError());

  const clearEditUserError = () => dispatch(resetEditUserError());

  const handleModalCancel = () => {
    setShowConfirmationModal(false);
  };

  const handleModalDiscard = () => {
    setShowConfirmationModal(false);
    handleClose("discard");
  };

  return (
    <>
      <MotifModal
        show={show}
        onClose={handleModalClose}
        className="admin-support-modal"
        size="lg"
        variant="slideout"
        focusTrapPaused={showConfirmationModal}
      >
        <MotifModalHeader>
          {isEdit ? "Edit admin & support user" : "Add admin & support user"}
        </MotifModalHeader>
        <MotifModalBody>
          {isAdminListLoading && (
            <MotifProgressLoader
              aria-busy="true"
              aria-label="This page is loading"
              aria-live="polite"
              arialabel="This page is loading"
              show
              variant="default"
              className="admin-list-progress"
            />
          )}
          {!isAdminListLoading && adminListError && (
            <ErrorOrderPage
              image={productListEmptyImg}
              header="An error has occurred while attempting to retrieve data. Please choose the option to refresh the page."
              handleRefresh={() => {
                dispatch(
                  fetchAdminSupportListData({
                    page: 1,
                    record: 1000,
                    search: "",
                    sortOrder: "ASC",
                    sortColumn: "Name",
                    target: "modal",
                  }),
                );
              }}
            />
          )}
          {!isAdminListLoading && !adminListError && (
            <div className="admin-support-modal-content">
              <MotifFormField className="admin-support-user-field">
                <label htmlFor={`admin-support-user`}>User</label>
                <SearchInput
                  key={`${selectedUser?.email ?? "user-search"}-${searchInputKey}`}
                  aria-label="Search for a user"
                  id={`admin-support-user-search`}
                  placeholder="Type a username or email"
                  open={!userListLoading}
                  disabled={isEdit}
                  className="admin-support-user-search"
                  items={
                    isExistingUser
                      ? []
                      : Array.isArray(userList)
                        ? userList
                            .filter((userData: any) => {
                              const emailRegex =
                                /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
                              return emailRegex.test(userData.email);
                            })
                            .map((userData: any) => formatUserOption(userData))
                        : []
                  }
                  onChange={handleSearchChange}
                  onSelect={handleUserSelect}
                  visibleOptions={10}
                  value={userSearch}
                  multiple={false}
                  name="admin-support-user"
                  clearSearch={handleClearSearch}
                />
                {userSearch.trim().length > 3 &&
                  !userListLoading &&
                  userList &&
                  !displayItems.length &&
                  !selectedUser &&
                  !userValidationError && (
                    <MotifErrorMessage
                      aria-live="assertive"
                      role="alert"
                      id="aria-error-primaryList"
                    >
                      Please enter valid user name or email.
                    </MotifErrorMessage>
                  )}
                {userValidationError && (
                  <MotifErrorMessage
                    aria-live="assertive"
                    role="alert"
                    id="admin-support-user-validation-error"
                  >
                    {userValidationError}
                  </MotifErrorMessage>
                )}
              </MotifFormField>

              {selectedUser && !isExistingUser && (
                <>
                  <section className="admin-support-user-details">
                    <span>User details</span>
                    <div className="admin-support-details-card">
                      <div className="admin-support-detail">
                        <span>Name</span>
                        <span className="detail-value">
                          {selectedUser.displayName}
                        </span>
                      </div>
                      <div className="admin-support-detail admin-support-email">
                        <span>Email</span>
                        <span className="detail-value">
                          {selectedUser.email}
                        </span>
                      </div>
                      <div className="admin-support-active-user">
                        <span>Active user</span>
                        <MotifToggleSwitch
                          checked={isActive}
                          onChange={() => setIsActive((active) => !active)}
                          onLabel="On"
                          offLabel="Off"
                          id="admin-support-active-user"
                        />
                      </div>
                    </div>
                  </section>

                  <MotifFormField className="admin-support-role-field">
                    <label>Role</label>
                    <MotifRadioButtonGroup
                      name="admin-support-role"
                      className="admin-support-role-options"
                    >
                      <MotifRadioButton
                        id="admin-support-role-admin"
                        name="admin-support-role"
                        value="system_admin"
                        checked={selectedRole === "system_admin"}
                        onChange={(event) => {
                          setSelectedRole(event.target.value as "system_admin");
                          setRoleValidationError("");
                        }}
                      >
                        Admin
                      </MotifRadioButton>
                      <MotifRadioButton
                        id="admin-support-role-support"
                        name="admin-support-role"
                        value="system_support"
                        checked={selectedRole === "system_support"}
                        onChange={(event) => {
                          setSelectedRole(
                            event.target.value as "system_support",
                          );
                          setRoleValidationError("");
                        }}
                      >
                        Support
                      </MotifRadioButton>
                    </MotifRadioButtonGroup>
                    {roleValidationError && (
                      <MotifErrorMessage
                        aria-live="assertive"
                        role="alert"
                        id="admin-support-role-validation-error"
                      >
                        {roleValidationError}
                      </MotifErrorMessage>
                    )}
                  </MotifFormField>
                </>
              )}
            </div>
          )}
          {addUserError && (
            <MotifToast variant="error" onClose={clearAddUserError}>
              {addUserError}
            </MotifToast>
          )}
          {editUserError && (
            <MotifToast variant="error" onClose={clearEditUserError}>
              {editUserError}
            </MotifToast>
          )}
        </MotifModalBody>
        <MotifModalFooter>
          <MotifButton
            variant="primary-alt"
            size="medium"
            onClick={handleModalClose}
            disabled={isAddingUser || isEditingUser}
          >
            Cancel
          </MotifButton>
          <MotifButton
            variant="primary"
            size="medium"
            onClick={isEdit ? handleEdit : handleAdd}
            loading={isAddingUser || isEditingUser}
            disabled={Boolean(adminListError) || isAddingUser || isEditingUser}
          >
            {isEdit ? "Save changes" : "Add"}
          </MotifButton>
        </MotifModalFooter>
      </MotifModal>

      <ConfirmationModal
        isVisible={showConfirmationModal}
        setIsVisible={setShowConfirmationModal}
        header="Discard changes?"
        message="If you leave now, your changes will be lost."
        cancelLabel="Keep working"
        confirmLabel="Discard changes"
        cancelButtonHandler={handleModalCancel}
        confirmButtonHandler={handleModalDiscard}
      />
    </>
  );
};

export default AdminSupportModal;
